import type { InvitationPreview } from '@escala/contracts';
import { ArrowRight, CircleCheck, Clock, Lock, Mail } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { Button } from '@/design-system/ui/Button';
import { Input } from '@/design-system/ui/Input';
import { Label } from '@/design-system/ui/Label';
import { Skeleton } from '@/design-system/ui/Skeleton';
import { LegalLink } from '@/components/Privacy';
import { CheckboxMark } from '@/design-system/ui/Checkbox';
import { cn } from '@/design-system/lib/cn';
import { INVITE_DAYS } from '@escala/domain/access';
import { LEGAL_VERSION } from '@escala/domain/legal';
import { acceptInvitation, invitationPreview } from '@/services/api/session';

import { authErrorMessage } from './authError';
import AuthFrame from './components/AuthFrame';
import NewPasswordForm from './components/NewPasswordForm';

type Phase =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  | { kind: 'preview'; preview: InvitationPreview }
  | { kind: 'done'; preview: InvitationPreview };

/** Cartão de situação (ativado, expirado, já usado, inválido): ícone, título, texto e ação. */
function Outcome({
  icon,
  tone,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  tone: 'success' | 'danger' | 'primary';
  title: string;
  children: ReactNode;
  action: ReactNode;
}) {
  const tile = {
    success: 'bg-success-soft text-success',
    danger: 'bg-danger-soft text-status-conflict',
    primary: 'bg-primary-soft text-primary',
  }[tone];

  return (
    <div
      role={tone === 'success' ? 'status' : 'alert'}
      className="flex flex-col items-center gap-4.5 text-center"
    >
      <span className={`grid size-14 place-items-center rounded-block ${tile} [&_svg]:size-6.5`}>{icon}</span>
      <h1 className="m-0 text-title-sm font-extrabold tracking-[-0.03em]">{title}</h1>
      <p className="m-0 text-body text-ink-secondary">{children}</p>
      {action}
    </div>
  );
}

/**
 * Convite (`/convite?token=…`): mostra quem convidou e o e-mail, e o convidado cria a
 * senha — o que já abre a sessão. Link vencido, já usado ou substituído por um convite
 * mais novo mostra o que fazer.
 */
export default function Invitation({
  token,
  onDone,
}: {
  token: string;
  onDone: (signedIn: boolean) => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [terms, setTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);

  useEffect(() => {
    let active = true;

    invitationPreview(token)
      .then((preview) => active && setPhase({ kind: 'preview', preview }))
      .catch((err: unknown) => active && setPhase({ kind: 'failed', message: authErrorMessage(err) }));

    return () => {
      active = false;
    };
  }, [token]);

  const toLogin = (
    <Button variant="secondary" className="h-11" onClick={() => onDone(false)}>
      Ir para o login
    </Button>
  );

  if (phase.kind === 'loading') {
    return (
      <AuthFrame title="Convite">
        <div role="status" aria-label="Carregando o convite" className="flex flex-col gap-3">
          <Skeleton className="h-6 w-2/5" />
          <Skeleton className="h-8 w-4/5" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      </AuthFrame>
    );
  }

  if (phase.kind === 'failed') {
    return (
      <AuthFrame title="Convite">
        <Outcome
          icon={<Clock aria-hidden />}
          tone="danger"
          title="Não foi possível abrir o convite"
          action={
            <Button variant="secondary" className="h-11" onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          }
        >
          {phase.message}
        </Outcome>
      </AuthFrame>
    );
  }

  const { preview } = phase;

  if (phase.kind === 'done') {
    return (
      <AuthFrame title="Acesso ativado">
        <Outcome
          icon={<CircleCheck aria-hidden />}
          tone="success"
          title="Acesso ativado"
          action={
            <Button className="h-11" onClick={() => onDone(true)}>
              Entrar no Live Gospel
              <ArrowRight aria-hidden />
            </Button>
          }
        >
          Pronto, {preview.firstName}! A partir de agora você entra com <strong>{preview.email}</strong> e a
          senha que acabou de criar.
        </Outcome>
      </AuthFrame>
    );
  }

  if (preview.status === 'expired') {
    return (
      <AuthFrame title="Convite expirado">
        <Outcome icon={<Clock aria-hidden />} tone="danger" title="Este convite expirou" action={toLogin}>
          O link vale por {INVITE_DAYS} dias. Peça ao administrador para enviar um novo convite para{' '}
          {preview.email}.
        </Outcome>
      </AuthFrame>
    );
  }

  if (preview.status === 'used') {
    return (
      <AuthFrame title="Convite já utilizado">
        <Outcome icon={<Lock aria-hidden />} tone="primary" title="Convite já utilizado" action={toLogin}>
          Esta conta já tem senha. Entre pelo login ou use “Esqueci minha senha”.
        </Outcome>
      </AuthFrame>
    );
  }

  if (preview.status === 'invalid') {
    return (
      <AuthFrame title="Convite inválido">
        <Outcome icon={<Lock aria-hidden />} tone="danger" title="Convite inválido" action={toLogin}>
          Este link não é mais válido. Se recebeu um convite mais novo, use o link dele.
        </Outcome>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Crie sua senha">
      <div className="flex flex-col gap-1.5">
        <span className="inline-flex h-6.5 items-center gap-1.5 self-start rounded-pill bg-primary-soft px-2.5 text-caption font-semibold text-primary-hover [&_svg]:size-3.5">
          <Mail aria-hidden />
          Convite de {preview.inviter}
        </span>
        <h1 className="m-0 mt-1.5 text-display-lg leading-tight font-extrabold tracking-[-0.03em]">
          Olá, {preview.firstName}! Crie sua senha
        </h1>
        <p className="m-0 text-body text-ink-subtle">
          Com ela você vai entrar no Live Gospel para ver suas escalas e manter sua disponibilidade em dia.
        </p>
      </div>
      <NewPasswordForm
        before={
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">E-mail</Label>
            <Input
              id="invite-email"
              value={preview.email}
              readOnly
              aria-readonly
              className="h-11 bg-surface-subtle text-ink-secondary"
            />
          </div>
        }
        action={
          <>
            Criar senha e ativar acesso
            <ArrowRight aria-hidden />
          </>
        }
        after={
          <div className="flex flex-col gap-1.5">
            <label
              className={cn(
                'flex cursor-pointer items-start gap-2.5 rounded-block border bg-white px-3.5 py-3 text-left',
                'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-primary',
                termsError ? 'border-status-conflict' : 'border-line-input'
              )}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={terms}
                aria-invalid={termsError}
                aria-describedby={termsError ? 'invite-terms-error' : undefined}
                onChange={(e) => {
                  setTerms(e.target.checked);
                  setTermsError(false);
                }}
              />
              <CheckboxMark checked={terms} className="mt-0.5" />
              <span className="text-small text-pretty text-ink-secondary">
                Li e aceito os <LegalLink doc="terms">Termos de Uso</LegalLink> e a{' '}
                <LegalLink doc="privacy">Política de Privacidade</LegalLink>, incluindo o tratamento da minha
                participação na igreja.
              </span>
            </label>
            {termsError ? (
              <span id="invite-terms-error" role="alert" className="text-caption text-status-conflict">
                Para ativar o acesso, aceite os Termos e a Política de Privacidade.
              </span>
            ) : null}
          </div>
        }
        guard={() => {
          setTermsError(!terms);

          return terms;
        }}
        onSubmit={async (password) => {
          await acceptInvitation(token, password, LEGAL_VERSION);
          setPhase({ kind: 'done', preview });
        }}
      />
    </AuthFrame>
  );
}
